/**
 * Gumroad License Validator
 * Implements license validation using Gumroad's API
 */

import type {
  LicenseInfo,
  LicenseValidationResult,
  LicenseActivationResult,
  OfflineGracePeriodStatus,
  DeviceInfo,
  LicenseValidationError,
  LicenseValidationErrorType
} from '$lib/types/subscription';

import type {
  LicenseValidator,
  LicenseValidatorConfig,
  LicenseProgressCallback,
  SecureLicenseStorage,
  DeviceLimitManager,
  OfflineGracePeriodManager
} from './interfaces';

import { LICENSE_CONFIG } from '$lib/constants/subscription';

/**
 * Gumroad API response interface
 */
interface GumroadLicenseResponse {
  success: boolean;
  uses: number;
  purchase: {
    seller_id: string;
    product_id: string;
    product_name: string;
    permalink: string;
    email: string;
    price: number;
    gumroad_fee: number;
    currency: string;
    quantity: number;
    discover_fee_charged: boolean;
    can_contact: boolean;
    referrer: string;
    card: {
      visual: string;
      type: string;
      bin: string;
      expiry_month: string;
      expiry_year: string;
    };
    order_number: number;
    sale_id: string;
    sale_timestamp: string;
    purchaser_id: string;
    subscription_id?: string;
    cancelled: boolean;
    disputed: boolean;
    dispute_won: boolean;
    id: string;
    created_at: string;
    updated_at: string;
    license_key: string;
    subscription_ended_at?: string;
    subscription_cancelled_at?: string;
    subscription_failed_at?: string;
  };
}

export class GumroadLicenseValidator implements LicenseValidator {
  private readonly config: LicenseValidatorConfig;
  private readonly storage: SecureLicenseStorage;
  private readonly deviceManager: DeviceLimitManager;
  private readonly gracePeriodManager: OfflineGracePeriodManager;
  
  // Cache for validation results
  private validationCache = new Map<string, { result: LicenseValidationResult; expiresAt: number }>();
  
  // Rate limiting
  private lastValidationTime = 0;
  private validationQueue = new Set<string>();

  constructor(
    storage: SecureLicenseStorage,
    deviceManager: DeviceLimitManager,
    gracePeriodManager: OfflineGracePeriodManager,
    config?: Partial<LicenseValidatorConfig>
  ) {
    this.storage = storage;
    this.deviceManager = deviceManager;
    this.gracePeriodManager = gracePeriodManager;
    
    this.config = {
      apiUrl: LICENSE_CONFIG.GUMROAD_API_URL,
      timeout: 30000, // 30 seconds
      retryAttempts: 3,
      retryDelay: 1000, // 1 second base delay
      maxConcurrentValidations: 2,
      cacheTtl: LICENSE_CONFIG.VALIDATION_INTERVAL_MS,
      deviceFingerprinting: true,
      ...config
    };
  }

  /**
   * Validate a license key with Gumroad API
   */
  async validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
    try {
      // Check cache first
      const cached = this.getCachedValidation(licenseKey);
      if (cached) {
        return cached;
      }

      // Check if we're already validating this key
      if (this.validationQueue.has(licenseKey)) {
        await this.waitForValidation(licenseKey);
        return this.getCachedValidation(licenseKey) || this.createErrorResult('validation_failed', 'Concurrent validation failed');
      }

      this.validationQueue.add(licenseKey);

      try {
        // Try online validation first
        const result = await this.validateOnline(licenseKey);
        
        // Cache successful result
        if (result.isValid) {
          this.cacheValidationResult(licenseKey, result);
          
          // Store license info securely
          if (result.license) {
            await this.storage.storeLicense(licenseKey, result.license);
          }
        }
        
        return result;
      } catch (error: any) {
        console.warn('Online validation failed, checking offline:', error);
        
        // For rate limit errors, immediately try offline without further retries
        if (error.message?.includes('429')) {
          const offlineResult = await this.validateOffline(licenseKey);
          if (offlineResult.isValid) {
            return offlineResult;
          }
          
          // Return rate limit error if offline also fails
          return {
            isValid: false,
            validatedAt: new Date(),
            error: {
              type: 'rate_limited',
              message: '요청이 너무 많습니다. 나중에 다시 시도해주세요.',
              code: 'RATE_LIMIT',
              retryable: true
            }
          };
        }
        
        // Try offline validation for other errors
        const offlineResult = await this.validateOffline(licenseKey);
        if (offlineResult.isValid) {
          return offlineResult;
        }
        
        // Return error if both online and offline failed
        return this.createErrorResult('network_error', `Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      this.validationQueue.delete(licenseKey);
    }
  }

  /**
   * Validate license online with Gumroad API
   */
  private async validateOnline(licenseKey: string): Promise<LicenseValidationResult> {
    // Implement rate limiting
    const now = Date.now();
    const timeSinceLastValidation = now - this.lastValidationTime;
    if (timeSinceLastValidation < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastValidation));
    }
    
    this.lastValidationTime = Date.now();

    // Extract product ID from license key format or use configured product ID
    const productId = this.extractProductId(licenseKey);
    
    const response = await this.makeGumroadRequest(productId, licenseKey);
    
    if (!response.success) {
      return this.createErrorResult('invalid_license', 'License validation failed');
    }

    // Convert Gumroad response to our license format
    const licenseInfo = await this.convertGumroadResponseToLicense(response, licenseKey);
    
    // Validate license status
    const validationErrors = this.validateLicenseStatus(licenseInfo, response);
    if (validationErrors.length > 0) {
      return this.createErrorResult('invalid_license', validationErrors.join(', '));
    }

    return {
      isValid: true,
      license: licenseInfo,
      fromCache: false,
      validatedAt: new Date()
    };
  }

  /**
   * Make HTTP request to Gumroad API through our server proxy
   */
  private async makeGumroadRequest(productId: string, licenseKey: string): Promise<GumroadLicenseResponse> {
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < this.config.retryAttempts; attempt++) {
      try {
        // Use server proxy for Gumroad validation
        const proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://appstore.junlim.org';
        const response = await fetch(`${proxyUrl}/api/license/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'User-Agent': 'Korean-Grammar-Checker/1.0'
          },
          body: JSON.stringify({
            licenseKey
          }),
          signal: AbortSignal.timeout(this.config.timeout)
        });

        if (!response.ok) {
          // Special handling for rate limit
          if (response.status === 429) {
            // Wait longer for rate limit errors
            if (attempt < this.config.retryAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)));
              continue;
            }
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Convert server response to Gumroad format
        if (data.valid) {
          return {
            success: true,
            uses: data.uses || 0,
            purchase: {
              seller_id: '',
              product_id: data.productId,
              product_name: data.productName,
              permalink: '',
              email: data.email,
              price: data.price,
              gumroad_fee: 0,
              currency: data.currency,
              quantity: 1,
              discover_fee_charged: false,
              can_contact: true,
              referrer: '',
              card: {
                visual: '',
                type: '',
                bin: '',
                expiry_month: '',
                expiry_year: ''
              },
              order_number: data.orderId || 0,
              sale_id: data.saleId,
              sale_timestamp: data.purchaseDate,
              purchaser_id: '',
              subscription_id: data.subscriptionId,
              cancelled: data.cancelled || false,
              disputed: data.disputed || false,
              dispute_won: false,
              id: data.saleId,
              created_at: data.purchaseDate,
              updated_at: data.purchaseDate,
              license_key: licenseKey,
              subscription_ended_at: data.expiresAt || data.subscriptionEndedAt,
              subscription_cancelled_at: data.cancelledAt || data.subscriptionCancelledAt,
              subscription_failed_at: data.subscriptionFailedAt
            }
          };
        } else {
          return {
            success: false,
            uses: 0,
            purchase: null as any
          };
        }
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        
        if (attempt < this.config.retryAttempts - 1) {
          const delay = this.config.retryDelay * Math.pow(2, attempt); // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError || new Error('Failed to validate license after retries');
  }

  /**
   * Validate license offline using cached data
   */
  private async validateOffline(licenseKey: string): Promise<LicenseValidationResult> {
    // Check if we have cached license data
    const cachedLicense = await this.storage.getLicense(licenseKey);
    if (!cachedLicense) {
      return this.createErrorResult('invalid_license', 'No cached license data available');
    }

    // Check grace period
    const gracePeriodStatus = await this.gracePeriodManager.getGracePeriodStatus(licenseKey);
    if (!gracePeriodStatus.isInGracePeriod) {
      return this.createErrorResult('network_error', 'Offline grace period has expired');
    }

    // Validate cached license
    const now = new Date();
    if (cachedLicense.expiresAt && now > cachedLicense.expiresAt) {
      return this.createErrorResult('expired_license', 'Cached license has expired');
    }

    return {
      isValid: true,
      license: cachedLicense,
      fromCache: true,
      validatedAt: new Date()
    };
  }

  /**
   * Activate license on current device
   */
  async activateLicense(
    licenseKey: string, 
    deviceInfo: DeviceInfo, 
    progressCallback?: LicenseProgressCallback
  ): Promise<LicenseActivationResult> {
    try {
      progressCallback?.({ stage: 'validation', message: 'Validating license...', progress: 10 });

      // First validate the license
      const validationResult = await this.validateLicense(licenseKey);
      if (!validationResult.isValid || !validationResult.license) {
        return {
          success: false,
          error: validationResult.error || this.createError('invalid_license', 'License validation failed')
        };
      }

      progressCallback?.({ stage: 'validation', message: 'License validated successfully', progress: 30 });

      // Check device limits
      progressCallback?.({ stage: 'activation', message: 'Checking device limits...', progress: 50 });
      
      const canActivate = await this.canActivateOnDevice(licenseKey, deviceInfo);
      if (!canActivate) {
        const devices = await this.deviceManager.getActiveDevices(licenseKey);
        return {
          success: false,
          requiresDeviceDeactivation: true,
          error: this.createError('device_limit_exceeded', 'Maximum device limit reached'),
          deviceInfo
        };
      }

      // Add device locally
      progressCallback?.({ stage: 'activation', message: 'Activating on device...', progress: 70 });
      
      const deviceAdded = await this.deviceManager.addDevice(licenseKey, deviceInfo);
      if (!deviceAdded) {
        return {
          success: false,
          error: this.createError('validation_failed', 'Failed to add device')
        };
      }

      // Register device on server
      progressCallback?.({ stage: 'activation', message: 'Registering device on server...', progress: 80 });
      
      try {
        const { ServerLicenseService } = await import('./ServerLicenseService');
        const serverService = new ServerLicenseService();
        const serverResult = await serverService.activateLicense(licenseKey, deviceInfo);
        
        if (!serverResult.success) {
          console.warn('Failed to register device on server:', serverResult.error);
          // Continue anyway since local activation succeeded
        }
      } catch (error) {
        console.warn('Failed to register device on server:', error);
        // Continue anyway since local activation succeeded
      }

      // Store license
      progressCallback?.({ stage: 'storage', message: 'Storing license data...', progress: 90 });
      
      await this.storage.storeLicense(licenseKey, validationResult.license);

      progressCallback?.({ stage: 'cleanup', message: 'Activation complete', progress: 100 });

      return {
        success: true,
        license: validationResult.license,
        deviceInfo
      };

    } catch (error) {
      return {
        success: false,
        error: this.createError('unknown_error', error instanceof Error ? error.message : 'Activation failed')
      };
    }
  }

  /**
   * Deactivate license on device
   */
  async deactivateLicense(licenseKey: string, deviceId?: string): Promise<boolean> {
    try {
      const targetDeviceId = deviceId || (await this.deviceManager.getCurrentDeviceInfo()).deviceId;
      
      // Call server to deactivate
      const { ServerLicenseService } = await import('./ServerLicenseService');
      const serverService = new ServerLicenseService();
      const result = await serverService.deactivateLicense(licenseKey, targetDeviceId);
      
      if (result.success) {
        // Remove from local storage
        await this.storage.removeLicense(licenseKey);
        await this.deviceManager.removeDevice(licenseKey, targetDeviceId);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to deactivate license:', error);
      return false;
    }
  }

  /**
   * Check if license can be validated offline
   */
  async canValidateOffline(licenseKey: string): Promise<boolean> {
    const hasCache = await this.storage.hasLicense(licenseKey);
    const inGracePeriod = await this.gracePeriodManager.isInGracePeriod(licenseKey);
    return hasCache && inGracePeriod;
  }

  /**
   * Get offline grace period status
   */
  async getOfflineGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    return await this.gracePeriodManager.getGracePeriodStatus(licenseKey);
  }

  /**
   * Refresh cached license data
   */
  async refreshLicenseCache(licenseKey: string): Promise<LicenseValidationResult> {
    // Clear cache and validate fresh
    this.validationCache.delete(licenseKey);
    return await this.validateLicense(licenseKey);
  }

  /**
   * Get device information for license
   */
  async getDeviceInfo(licenseKey: string): Promise<DeviceInfo[]> {
    return await this.deviceManager.getActiveDevices(licenseKey);
  }

  /**
   * Check if device can be activated
   */
  async canActivateOnDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    const cachedLicense = await this.storage.getLicense(licenseKey);

    // If no cached license (first activation), use default max devices from config
    // This allows the first activation to proceed
    const maxDevices = cachedLicense?.maxDevices || LICENSE_CONFIG.MAX_DEVICES;

    return await this.deviceManager.canAddDevice(licenseKey, maxDevices);
  }

  /**
   * Helper methods
   */
  
  private getCachedValidation(licenseKey: string): LicenseValidationResult | null {
    const cached = this.validationCache.get(licenseKey);
    if (cached && Date.now() < cached.expiresAt) {
      return cached.result;
    }
    return null;
  }

  private cacheValidationResult(licenseKey: string, result: LicenseValidationResult): void {
    this.validationCache.set(licenseKey, {
      result,
      expiresAt: Date.now() + this.config.cacheTtl
    });
  }

  private async waitForValidation(licenseKey: string): Promise<void> {
    const maxWait = 30000; // 30 seconds
    const startTime = Date.now();
    
    while (this.validationQueue.has(licenseKey) && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  private extractProductId(licenseKey: string): string {
    // Product ID is now handled server-side for security
    // Server has the actual GUMROAD_PRODUCT_ID in environment variables
    return 'server-managed';
  }

  /**
   * Calculate if license is active based on purchase status
   */
  private calculateActiveStatus(purchase: GumroadLicenseResponse['purchase']): boolean {
    // 분쟁 중이면 비활성화
    if (purchase.disputed) {
      return false;
    }

    // 구독이 취소된 경우
    if (purchase.cancelled) {
      // 만료일이 있는 경우 현재 시간과 비교
      if (purchase.subscription_ended_at) {
        const now = new Date();
        const expiresAt = new Date(purchase.subscription_ended_at);
        return now < expiresAt; // 만료일 전까지는 활성 상태
      }
      // 만료일 정보가 없으면 비활성화
      return false;
    }

    // 취소되지 않고 분쟉 중이 아니면 활성화
    return true;
  }

  private async convertGumroadResponseToLicense(
    response: GumroadLicenseResponse, 
    licenseKey: string
  ): Promise<LicenseInfo> {
    const purchase = response.purchase;
    const currentDevice = await this.deviceManager.getCurrentDeviceInfo();
    
    return {
      licenseKey,
      provider: 'gumroad',
      productId: purchase.product_id,
      productName: purchase.product_name,
      email: purchase.email,
      purchaseDate: new Date(purchase.created_at),
      expiresAt: purchase.subscription_ended_at ? new Date(purchase.subscription_ended_at) : undefined,
      uses: response.uses,
      maxUses: undefined, // Gumroad doesn't have a hard max uses limit
      devices: [currentDevice], // Will be updated by device manager
      maxDevices: LICENSE_CONFIG.MAX_DEVICES, // Default, could be determined by product
      isActive: this.calculateActiveStatus(purchase),
      lastValidatedAt: new Date(),
      metadata: {
        saleId: purchase.sale_id,
        purchaserId: purchase.purchaser_id,
        subscriptionId: purchase.subscription_id,
        orderNumber: purchase.order_number,
        price: purchase.price,
        currency: purchase.currency
      }
    };
  }

  private validateLicenseStatus(license: LicenseInfo, response: GumroadLicenseResponse): string[] {
    const errors: string[] = [];
    const purchase = response.purchase;

    // Check for disputes and chargebacks (hard failures)
    if (purchase.disputed) {
      errors.push('License is under dispute');
    }

    // Check if license is currently active
    // Note: calculateActiveStatus already handles cancelled subscriptions correctly
    // (active until subscription_ended_at date)
    if (!license.isActive) {
      if (purchase.cancelled && license.expiresAt) {
        errors.push(`License subscription was cancelled and expired on ${license.expiresAt.toISOString()}`);
      } else if (purchase.cancelled) {
        errors.push('License has been cancelled');
      } else if (license.expiresAt && new Date() > license.expiresAt) {
        errors.push('License has expired');
      } else {
        errors.push('License is not active');
      }
    }

    return errors;
  }

  private createErrorResult(type: LicenseValidationErrorType, message: string): LicenseValidationResult {
    return {
      isValid: false,
      error: this.createError(type, message),
      validatedAt: new Date()
    };
  }

  private createError(type: LicenseValidationErrorType, message: string): LicenseValidationError {
    const retryableErrors: LicenseValidationErrorType[] = ['network_error', 'rate_limited', 'server_error'];
    
    return {
      type,
      message,
      retryable: retryableErrors.includes(type),
      details: {}
    };
  }
}